import React, { useEffect, useState } from "react";
import { useGetCardToUploadByUserIdQuery } from "../../services/myServer/cardToUpload";
import { useSelector } from "react-redux";
import { selectGame, selectToken } from "../../store/slices/global";
import {
  CardToSyncByUserIdCardType,
  CardToSyncStatusEnum,
  CardToUploadByUserIdCardType,
  CardTraderConditionValueType,
  CardTraderLanguageValueType,
  GameType,
} from "magic-shared-types";
import { useGetMyInventoryQuery } from "../../services/cardtrader/cardtraderApi";
import { useGetCardToSyncByUserQuery } from "../../services/myServer/CardToSync";
import { PrimaryButton } from "../../components/common/common.style";
import {
  ScrollContainer,
  StyledTable,
  Td,
  Th,
} from "../../components/TransactionPageComponents/SectionUploadCards/TransactionCreateCards.styled";
import { Langugage } from "../../components/Cards/Language";
import { Condition } from "../../components/Cards/Condition";
import { Box } from "../../components/primitives";
import { useCards } from "../../context/CardsContext";

type BaseCardObject = {
  card_name: string;
  blue_print_id: number;
  card_market_id?: number | null;
  language: CardTraderLanguageValueType;
  quantity: number;
  condition: CardTraderConditionValueType;
  is_foil?: boolean;
  is_signed?: boolean;
  is_altered?: boolean;
  is_first_edition?: boolean;
  is_reverse?: boolean;
  description?: string | null;
};

export const SanityCheckPage = () => {
  const game = useSelector(selectGame);
  const pageSize = 200;
  const [pageUpload, setPageUpload] = useState(1);
  const [pageSync, setPageSync] = useState(1);
  const [completedUpload, setCompletedUpload] = useState(false);
  const [completedSync, setCompletedSync] = useState(false);
  const [cardsDifference, setCardsDifferece] = useState<BaseCardObject[]>([]);
  const [allCardsUploaded, setAllCardsUploaded] = useState<
    CardToUploadByUserIdCardType[]
  >([]);
  const [allCardsSync, setAllCardsSync] = useState<
    CardToSyncByUserIdCardType[]
  >([]);
  const token = useSelector(selectToken);
  const hasFoil = !["yugioh", "onepiece", "pokemon"].includes(
    game.card_trader_game
  );
  const hasFirstEdition = ["yugioh", "pokemon"].includes(game.card_trader_game);
  const hasReverse = ["pokemon"].includes(game.card_trader_game);
  const foundCardComplexArray = useCards();

  const { data: myInvetory } = useGetMyInventoryQuery(
    { token },
    { skip: !token }
  );

  const { data: cardToUploadData } = useGetCardToUploadByUserIdQuery(
    {
      page: pageUpload,
      pageSize,
      status_ct: undefined,
      status_cm: undefined,
      game_id: game.id,
    },
    { skip: !game }
  );

  const { data: cardToSyncData } = useGetCardToSyncByUserQuery({
    page: pageSync,
    pageSize,
    status: CardToSyncStatusEnum.SUCCESS,
    source: undefined,
    game_id: game?.id,
  });

  useEffect(() => {
    if (cardToUploadData?.data?.length) {
      setAllCardsUploaded((prev) => [...prev, ...cardToUploadData.data]);
      if (cardToUploadData.data.length === pageSize) {
        setPageUpload((prev) => prev + 1);
      } else {
        setCompletedUpload(true);
      }
    }
  }, [cardToUploadData]);

  useEffect(() => {
    if (cardToSyncData?.data?.length) {
      setAllCardsSync((prev) => [...prev, ...cardToSyncData.data]);
      if (cardToSyncData.data.length === pageSize) {
        setPageSync((prev) => prev + 1);
      } else {
        setCompletedSync(true);
      }
    }
  }, [cardToSyncData]);

  const buildCardKey = (card: BaseCardObject) =>
    JSON.stringify({
      blue_print_id: card.blue_print_id,
      condition: card.condition,
      is_foil: card.is_foil || false,
      description: card.description || "",
      language: card.language,
      is_signed: card.is_signed || false,
      is_first_edition: card.is_first_edition || false,
      is_reverse: card.is_reverse || false,
      is_altered: card.is_altered || false,
    });

  const mergeCards = (cards: BaseCardObject[]) => {
    const merged = new Map<string, BaseCardObject>();

    for (const card of cards) {
      const key = buildCardKey(card);

      const existing = merged.get(key);

      if (existing) {
        existing.quantity += card.quantity;
      } else {
        merged.set(key, card);
      }
    }

    return Array.from(merged.values());
  };

  const diffCards = (
    mergedCards: BaseCardObject[],
    mergedInventory: BaseCardObject[]
  ) => {
    const diffs: BaseCardObject[] = [];

    // Build maps for both sides
    const invMap = new Map<string, BaseCardObject>();
    for (const card of mergedInventory) {
      const key = buildCardKey(card);
      invMap.set(key, card);
    }

    const cardMap = new Map<string, BaseCardObject>();
    for (const card of mergedCards) {
      const key = buildCardKey(card);
      cardMap.set(key, card);
    }

    cardMap.forEach((card, key) => {
      const invCard = invMap.get(key);
      if (!invCard) {
        diffs.push({
          ...card,
          card_name: `${card.card_name} (missing in inventory)`,
        });
      } else if (invCard.quantity !== card.quantity) {
        diffs.push({
          ...card,
          card_name: `${card.card_name} (qty mismatch: expected ${invCard.quantity}, got ${card.quantity})`,
        });
      }
    });

    invMap.forEach((invCard, key) => {
      const card = cardMap.get(key);
      if (!card) {
        diffs.push({
          ...invCard,
          card_name: `${invCard.card_name} (extra in inventory, not in mergedCards)`,
        });
      }
    });

    return diffs;
  };

  // removeSyncFromUpload: subtract sync quantities from upload quantities
  const removeSyncFromUpload = (
    mergedUpload: BaseCardObject[],
    mergedSync: BaseCardObject[]
  ) => {
    // map upload cards by key -> cloned object we can mutate
    const uploadMap = new Map<string, BaseCardObject>();
    for (const u of mergedUpload) {
      uploadMap.set(buildCardKey(u), { ...u });
    }

    // subtract sync quantities from the upload map
    for (const s of mergedSync) {
      const key = buildCardKey(s);
      const uploadCard = uploadMap.get(key);
      if (!uploadCard) {
        // no matching upload card: nothing to subtract
        continue;
      }

      uploadCard.quantity -= s.quantity;

      if (uploadCard.quantity <= 0) {
        // fully consumed (or over-consumed) -> remove the entry
        uploadMap.delete(key);
      } else {
        // update the entry with the reduced quantity
        uploadMap.set(key, uploadCard);
      }
    }

    // remaining uploads (with updated quantities)
    return Array.from(uploadMap.values());
  };

  const onClickStartCheck = () => {
    const mappedUpload = allCardsUploaded.map((card) => {
      return {
        blue_print_id: card.blue_print_id,
        card_name: card.card_name,
        card_market_id: card.card_market_id,
        language: card.language,
        quantity: card.quantity,
        condition: card.condition,
        is_foil: card.is_foil,
        is_signed: card.is_signed,
        is_altered: card.is_altered,
        is_first_edition: card.is_first_edition,
        is_reverse: card.is_reverse,
        description: card.description,
      };
    });

    const mappedSync = allCardsSync.map((card) => {
      return {
        blue_print_id: card.blue_print_id,
        card_name: card.name,
        card_market_id: card.card_market_id,
        language: card.language,
        quantity: card.sold_quantity,
        condition: card.condition,
        is_foil: card.is_foil,
        is_signed: card.is_signed,
        is_altered: card.is_altered,
        is_first_edition: card.is_first_edition,
        is_reverse: card.is_reverse,
        description: card.description,
      };
    });

    console.log("mappedSync", mappedSync);

    const mappedInventory = myInvetory?.map((inventoryCard) => {
      const cardFound = foundCardComplexArray[inventoryCard.blueprint_id];
      return {
        blue_print_id: inventoryCard.blueprint_id,
        card_name: inventoryCard.name_en,
        card_market_id: cardFound.card_market_ids[0],
        language:
          inventoryCard?.properties_hash?.[
            `${game.card_trader_game as GameType}_language`
          ] || "en",
        quantity: inventoryCard.quantity,
        condition: inventoryCard.properties_hash.condition,
        is_foil:
          inventoryCard?.properties_hash?.[
            `${game.card_trader_game as GameType}_foil`
          ],
        is_signed: inventoryCard.properties_hash.signed || false,
        is_altered: inventoryCard.properties_hash.altered || false,
        is_first_edition: inventoryCard.properties_hash.first_edition || false,
        is_reverse: inventoryCard.properties_hash.pokemon_reverse || false,
        description: inventoryCard.description,
      };
    });

    const mergedCards = mergeCards([...mappedUpload]);
    const mergedSync = mergeCards([...mappedSync]);
    const allCardsUploadedMinusSync = removeSyncFromUpload(
      mergedCards,
      mergedSync
    );

    const mergedInventory =
      mappedInventory && mergeCards(mappedInventory || []);

    const difference = diffCards(
      allCardsUploadedMinusSync,
      mergedInventory || []
    );

    setCardsDifferece(difference);

    // merge all inventory cards together
    // check discrepancy between the two merged inventory
  };

  return (
    <Box
      css={{
        backgroundColor: "#f0fdf4",
        minHeight: "100vh",
        paddingBottom: 50,
      }}
    >
      <Box css={{ marginBottom: 40 }}>
        <p>Loaded upload {allCardsUploaded.length} cards</p>
        <p>Loaded sync {allCardsSync.length} cards</p>
        {completedSync && completedUpload && (
          <PrimaryButton
            onClick={() => {
              onClickStartCheck();
            }}
          >
            START CHECK
          </PrimaryButton>
        )}
      </Box>

      <ScrollContainer>
        <StyledTable>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Difference</Th>
              <Th>Blueprint ID</Th>
              <Th>Card Market ID</Th>
              <Th>Language</Th>
              <Th>Quantity</Th>
              <Th>Condition</Th>
              {hasFoil && <Th>Foil</Th>}
              {hasFirstEdition && <Th>First Edition</Th>}
              {hasReverse && <Th>Reverse</Th>}
              <Th>Signed</Th>
              <Th>Altered</Th>
              <Th>Description</Th>
            </tr>
          </thead>
          <tbody>
            {cardsDifference.map((card, idx) => (
              <tr key={idx}>
                {/* Name includes the diff type (missing / extra / mismatch) */}
                <Td>{card.card_name}</Td>
                <Td>
                  {card.card_name.includes("missing")
                    ? "❌ Missing in inventory"
                    : card.card_name.includes("extra")
                    ? "➕ Extra in inventory"
                    : card.card_name.includes("qty mismatch")
                    ? "⚠️ Quantity mismatch"
                    : "-"}
                </Td>

                {/* Blueprint link */}
                <Td>
                  <a
                    href={`https://www.cardtrader.com/cards/${card.blue_print_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {card.blue_print_id}
                  </a>
                </Td>

                {/* Card Market ID if present */}
                <Td>
                  {card.card_market_id ? (
                    <a
                      href={`https://www.cardmarket.com/en/${game.card_market_game}/Products?idProduct=${card.card_market_id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {card.card_market_id}
                    </a>
                  ) : (
                    "-"
                  )}
                </Td>

                {/* Language */}
                <Td>
                  <Langugage language={card.language} />
                </Td>

                {/* Quantity */}
                <Td>{card.quantity}</Td>

                {/* Condition */}
                <Td>
                  <Condition condition={card.condition} />
                </Td>

                {/* Extra flags */}
                {hasFoil && <Td>{card.is_foil ? "Yes" : "No"}</Td>}
                {hasFirstEdition && (
                  <Td>{card.is_first_edition ? "Yes" : "No"}</Td>
                )}
                {hasReverse && <Td>{card.is_reverse ? "Yes" : "No"}</Td>}
                <Td>{card.is_signed ? "Yes" : "No"}</Td>
                <Td>{card.is_altered ? "Yes" : "No"}</Td>

                {/* Description */}
                <Td css={{ width: 220 }}>{card.description || "-"}</Td>

                {/* Difference marker */}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </ScrollContainer>
    </Box>
  );
};
